import type { Button, TextLinkButton } from 'braid-design-system';
import { omit } from 'lodash';
import { createElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { READ } from 'src/consts';
import type { Mode, Card, Page } from 'src/types';

import { updateRoute, updatePage } from './actionCreators';

export type LinkComponent =
  | React.ComponentType<{ href: string; children: React.ReactNode }>
  | string;

interface Props {
  to?: Card;
  page?: Page;
  mode?: Mode;
  params?: Record<string, unknown>;
  component?: LinkComponent;
  className?: string;
  analytics?: Record<string, unknown>;
}

type LinkProps = Props &
  React.ComponentProps<typeof Button> &
  React.ComponentProps<typeof TextLinkButton>;

export const Link = ({
  children,
  component = 'a',
  ...restProps
}: LinkProps) => {
  const dispatch = useDispatch();

  const onClickFn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const {
        to,
        page,
        mode = READ,
        params = {},
        analytics = {},
        onClick,
      } = restProps;

      onClick?.(event);

      if (page) {
        dispatch(updatePage({ page, params, analytics }));

        if (to) {
          setTimeout(() => {
            dispatch(updateRoute({ to, mode, params, analytics }));
          });
        }
      } else if (to) {
        dispatch(
          updateRoute({
            to,
            mode,
            params,
            analytics,
          }),
        );
      }
    },
    [dispatch, restProps],
  );

  const props = {
    ...omit(
      restProps,
      'updateRoute',
      'updatePage',
      'to',
      'page',
      'mode',
      'params',
      'onClick',
      'analytics',
    ),
    onClick: onClickFn,
    children,
    href: '',
  };

  return createElement(component, props, children);
};
